import { useState } from 'react'
import { Button, Spacing } from '@big-red-group/storefront-common-ui'
import CustomPickup from './CustomPickup'
import RequiredPickup from './RequiredPickup'
import RadioButtonGroup, { RadioButtonOption } from './RadioButtonGroup'
import { Wrapper } from '@/components/PickupModal.styles'
import { BookingPickup, PickupType } from '@big-red-group/storefront-common-checkout'
import { getMetadataItem } from '@/helpers/PickupHelper'
import { ClickEventHandlerInput, TrackBookingFieldEnteredInput } from '@/types'

type PickupProps = {
  handlerKey: string | undefined
  sessionId: number
  availabilityId?: string
  isRequired: boolean
  onChange: (pickup: BookingPickup) => void
  previouslySelectedPickup?: BookingPickup
  bookingFieldEntered?: (bookingField: TrackBookingFieldEnteredInput) => void
  onButtonClick?: (params: ClickEventHandlerInput) => void
}

const PickupModal: React.FC<PickupProps> = ({
  onChange,
  handlerKey,
  sessionId,
  availabilityId,
  isRequired,
  previouslySelectedPickup,
  bookingFieldEntered,
  onButtonClick,
}) => {
  const initialPickupType = isRequired ? 'PRESET' : undefined
  // TODO add type
  const [pickupType, setPickupType] = useState<PickupType | undefined>(
    previouslySelectedPickup ? previouslySelectedPickup.type : initialPickupType
  )
  const [pickupLocation, setPickupLocation] = useState<BookingPickup | undefined>(previouslySelectedPickup)
  const [customPickupLocation, setCustomPickupLocation] = useState('')
  const requiredPickupOptions: RadioButtonOption[] = [
    {
      label: 'Choose pickup location',
      value: 'PRESET',
      children: (
        <RequiredPickup
          handlerKey={handlerKey}
          sessionId={sessionId}
          previouslySelectedPickupLocation={previouslySelectedPickup}
          onChange={setPickupLocation}
          availabilityId={availabilityId}
          bookingFieldEntered={bookingFieldEntered}
        />
      ),
      checked: isRequired,
    },
  ]

  const optionalPickupOptions: RadioButtonOption[] = [
    {
      label: "Can't find my location",
      value: 'CUSTOM',
      children: (
        <CustomPickup
          onChange={setCustomPickupLocation}
          location={customPickupLocation}
          bookingFieldEntered={bookingFieldEntered}
        />
      ),
      description:
        'Once your booking is confirmed, we will send you a confirmation voucher with the contact details of the local supplier for this activity. We recommend you confirm your pickup location and time with the local supplier at least 48 hours prior to your departure date. If you require further assistance our customer service representatives are available 7 days a week.',
    },
    {
      label: 'No pickup or advise later',
      value: 'NONE',
      description:
        'If you don’t know your hotel name and wish to advise us later, or you don’t require hotel pickup please select this option. If you wish to advise us later, we recommend your confirm your pickup location and time with the local supplier at least 48 hours prior to your departure date. If you require further assistance our customer service representatives are available 7 days a week.',
    },
  ]

  const getPickupInfo = (pickupType: PickupType) => {
    switch (pickupType) {
      case 'CUSTOM':
        return customPickupLocation
      case 'NONE':
        return 'No pickup'
      default:
        return ''
    }
  }

  const handleConfirm = (event?: any) => {
    if (pickupType === 'PRESET') {
      onChange(pickupLocation!)
      onButtonClick?.({ url: window.location.href, location: 'PDP | Booking | Pick up location', event })
      return
    }
    const pickupInfo = getPickupInfo(pickupType!)
    if (!pickupInfo) {
      return
    }
    onChange({
      id: null,
      address: pickupInfo,
      type: pickupType!,
      metadata: [{ key: 'info', value: pickupInfo }],
    })
    onButtonClick?.({ url: window.location.href, location: 'PDP | Booking | Pick up location', event })
  }

  return (
    <Wrapper>
      <RadioButtonGroup
        options={isRequired ? requiredPickupOptions : [...requiredPickupOptions, ...optionalPickupOptions]}
        onChange={(value) => {
          setPickupType(value as PickupType)
          bookingFieldEntered?.({
            label: 'ShippingMethod',
            value: value,
            section: 'Pick up location',
            fieldTitle: 'Pick up location',
            fieldType: 'radiobutton',
          })
        }}
        name="optional-pickup"
      />
      <Spacing size="XXS" />
      <Button
        dataTestId="pickup-submit"
        disabled={
          pickupType === undefined ||
          (pickupType === 'CUSTOM' && customPickupLocation === '') ||
          (pickupType === 'PRESET' && pickupLocation === undefined)
        }
        size="S"
        type="submit"
        onClick={(event) => handleConfirm(event)}
      >
        Confirm
      </Button>
    </Wrapper>
  )
}

export default PickupModal
